import * as React from 'react';
import { Check, Loader2, Lock } from 'lucide-react';

import { cn } from '@/lib/utils';

interface WizardOptionCardProps {
    title: string;
    description?: string;
    icon?: React.ReactNode;
    /** Optional integration logo URL — replaces the icon when provided. */
    logoUrl?: string;
    selected: boolean;
    disabled?: boolean;
    disabledReason?: React.ReactNode;
    /** While true, the card renders disabled with a spinner in place of the
     *  lock icon and suppresses the disabled-reason text. Use it for cards
     *  whose enablement depends on an in-flight async check. */
    loading?: boolean;
    onSelect: () => void;
}

/**
 * Shared option card used across every wizard step. Mirrors the styling of
 * the original FunnelTemplateDialog TemplateCard so the wizard feels like a
 * natural extension of the existing UI.
 */
export function WizardOptionCard({
    title,
    description,
    icon,
    logoUrl,
    selected,
    disabled,
    disabledReason,
    loading,
    onSelect,
}: WizardOptionCardProps) {
    const effectivelyDisabled = disabled || loading;
    return (
        <button
            type="button"
            onClick={() => {
                if (effectivelyDisabled) return;
                onSelect();
            }}
            // Use aria-disabled instead of the native `disabled` attribute so
            // clicks on inline children (e.g. the "Connect now" link in the
            // disabled-reason block) still fire — `disabled` would swallow
            // every click in the button subtree.
            aria-disabled={effectivelyDisabled || undefined}
            aria-busy={loading || undefined}
            className={cn(
                'group relative flex w-full flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-all',
                selected
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50',
                effectivelyDisabled && 'cursor-not-allowed opacity-60 hover:border-border hover:bg-transparent',
            )}
        >
            {selected && (
                <div className="bg-primary absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full">
                    <Check className="h-3 w-3 text-white" />
                </div>
            )}
            {loading && !selected && (
                <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-muted">
                    <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
                </div>
            )}
            {disabled && !loading && !selected && (
                <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-muted">
                    <Lock className="text-muted-foreground h-3 w-3" />
                </div>
            )}

            <div className="flex items-center gap-3">
                <div
                    className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-lg',
                        selected ? 'bg-primary/10' : 'bg-muted',
                        disabled && 'bg-muted/50',
                    )}
                >
                    {logoUrl ? (
                        <img src={logoUrl} alt={title} className="h-6 w-6" />
                    ) : (
                        <span className={cn(selected ? 'text-primary' : 'text-muted-foreground')}>{icon}</span>
                    )}
                </div>
                <span className="font-medium">{title}</span>
            </div>

            {description && (
                <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
            )}

            {disabled && !loading && disabledReason && (
                <div className="text-destructive text-xs">{disabledReason}</div>
            )}
        </button>
    );
}
